import { useContext, useState } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Textarea, Button, Avatar,
} from '@mui/joy';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';

import { AppContext } from '../../context';
import { APIFireEmployee, APIRestoreEmployee } from '../../api';

const getInitials = (acc) => {
    if (!acc) return '?';
    const f = acc.first_name?.[0] || '';
    const l = acc.last_name?.[0] || '';
    return (f + l).toUpperCase() || acc.email?.[0]?.toUpperCase() || '?';
};

/**
 * Модалка увольнения / восстановления сотрудника с двумя степенями
 * подтверждения и причиной.
 *
 * Props:
 *   open: boolean
 *   employee: { id, first_name, last_name, email, role, ... }
 *   mode: 'fire' | 'restore'
 *   onClose: () => void
 *   onSaved: () => void
 */
export default function FireEmployeeModal({ open, employee, mode = 'fire', onClose, onSaved }) {
    const { token, showToast } = useContext(AppContext);

    const [step,   setStep]   = useState(1);    // 1 = причина / 2 = финальное подтверждение
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const isRestore = mode === 'restore';

    const reset = () => {
        setStep(1);
        setReason('');
        setSaving(false);
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose();
    };

    const handleSubmit = async () => {
        if (!employee || !token) return;

        setSaving(true);
        let res;
        if (isRestore) {
            res = await APIRestoreEmployee(token, employee.id);
        } else {
            res = await APIFireEmployee(token, employee.id, reason.trim());
        }

        if (res?.error) {
            const msg = typeof res.error === 'string' ? res.error : (res.error.error || 'Ошибка');
            showToast(msg, 'danger');
            setSaving(false);
            return;
        }

        showToast(
            isRestore
                ? `${employee.full_name || employee.first_name} восстановлен`
                : `${employee.full_name || employee.first_name} уволен`,
            'success',
        );
        setSaving(false);
        reset();
        if (onSaved) onSaved();
    };

    if (!employee) return null;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
            <ModalDialog
                sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                    maxWidth: 440, width: '100%',
                    p: 3,
                    maxHeight: '90vh',      // высота по содержимому, не на весь экран
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isRestore
                            ? <RestoreRoundedIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                            : <LogoutRoundedIcon sx={{ fontSize: 20, color: '#f97316' }} />
                        }
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', letterSpacing: '-0.01em' }}>
                            {isRestore ? 'Восстановить сотрудника' : 'Уволить сотрудника'}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                        {isRestore
                            ? 'Сотрудник снова сможет войти в систему и получать уведомления.'
                            : 'Все данные сохранятся, но сотрудник не сможет войти в систему.'
                        }
                    </Typography>
                </Box>

                {/* ── Карточка сотрудника ─────────────────────────── */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 1.2, mb: 2,
                    borderRadius: 'var(--joy-radius-lg)',
                    border: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'var(--joy-palette-background-level2)',
                }}>
                    <Avatar src={employee.avatar_url} sx={{
                        width: 40, height: 40, fontSize: 14, fontWeight: 700,
                        bgcolor: 'rgba(162,38,226,0.12)',
                        color: 'var(--joy-palette-primary-300)',
                        border: '1px solid var(--joy-palette-divider)',
                    }}>
                        {(employee.first_name || employee.full_name || '').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                            {employee.full_name || `${employee.first_name} ${employee.last_name}`.trim() || employee.email}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                            {employee.email}
                        </Typography>
                    </Box>
                </Box>

                {/* ─────── ВОССТАНОВЛЕНИЕ ─────── */}
                {isRestore && (
                    <>
                        <Box sx={{
                            mb: 2, p: 1.5,
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid rgba(34,197,94,0.2)',
                            bgcolor: 'rgba(34,197,94,0.06)',
                        }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                После восстановления сотрудник сможет авторизоваться
                                и снова появится в списках для назначений и финансов.
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.2 }}>
                            <Button variant="outlined" color="neutral" onClick={handleClose}
                                disabled={saving} sx={{ flex: 1, fontWeight: 700 }}>
                                Отмена
                            </Button>
                            <Button variant="solid" color="success" onClick={handleSubmit}
                                loading={saving} sx={{ flex: 1, fontWeight: 700 }}>
                                Восстановить
                            </Button>
                        </Box>
                    </>
                )}

                {/* ─────── УВОЛЬНЕНИЕ: ШАГ 1 — Причина ─────── */}
                {!isRestore && step === 1 && (
                    <>
                        <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{
                                    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: 'var(--joy-palette-text-secondary)',
                                }}>
                                    Причина увольнения
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                    {reason.length}/500
                                </Typography>
                            </Box>
                            <Textarea
                                placeholder="Опишите причину (необязательно, но желательно для журнала)"
                                value={reason}
                                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                                minRows={3} maxRows={6}
                                sx={{
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    color: 'var(--joy-palette-text-primary)',
                                    fontSize: 13,
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.2, mt: 2 }}>
                            <Button variant="outlined" color="neutral" onClick={handleClose}
                                disabled={saving} sx={{ flex: 1, fontWeight: 700 }}>
                                Отмена
                            </Button>
                            <Button variant="solid" color="warning" onClick={() => setStep(2)}
                                sx={{ flex: 1, fontWeight: 700 }}>
                                Далее
                            </Button>
                        </Box>
                    </>
                )}

                {/* ─────── УВОЛЬНЕНИЕ: ШАГ 2 — Финальное подтверждение ─────── */}
                {!isRestore && step === 2 && (
                    <>
                        <Box sx={{
                            mb: 2, p: 1.5,
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid rgba(249,115,22,0.3)',
                            bgcolor: 'rgba(249,115,22,0.06)',
                        }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <WarningRoundedIcon sx={{ fontSize: 18, color: '#f97316', flexShrink: 0, mt: 0.2 }} />
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#f97316', mb: 0.5 }}>
                                        Подтвердите увольнение
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                        Сотрудник будет помечен как «уволен»: токены сессии
                                        сбросятся, push-подписки отключатся, в списках по
                                        умолчанию он показываться не будет. Данные (продажи,
                                        штрафы, премии) сохранятся — их можно будет видеть
                                        во вкладке «Уволенные» и в финансовых отчётах.
                                    </Typography>
                                    {reason.trim() && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(249,115,22,0.2)' }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', mb: 0.3 }}>
                                                Причина:
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', fontStyle: 'italic' }}>
                                                «{reason.trim()}»
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.2 }}>
                            <Button variant="outlined" color="neutral" onClick={() => setStep(1)}
                                disabled={saving} sx={{ flex: 1, fontWeight: 700 }}>
                                Назад
                            </Button>
                            <Button variant="solid" color="danger" onClick={handleSubmit}
                                loading={saving} sx={{ flex: 1, fontWeight: 700 }}>
                                Уволить
                            </Button>
                        </Box>
                    </>
                )}
            </ModalDialog>
        </Modal>
    );
}